import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { usePlatform } from '../../context/PlatformContext';
import { NotificationCard } from '../Notifications/NotificationCard';
import { readerNotifications, vendorNotifications } from '../../data/notificationsData';
import { CheckIcon, XIcon } from 'lucide-react';
interface NotificationDropdownProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  notificationButtonRef: React.RefObject<HTMLButtonElement>;
  isReader: boolean;
}
export const NotificationDropdown: React.FC<NotificationDropdownProps> = ({
  isOpen,
  setIsOpen,
  notificationButtonRef,
  isReader
}) => {
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const {
    isAfterTide
  } = usePlatform();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const notifications = isReader ? readerNotifications : vendorNotifications;
  const unreadCount = notifications.filter(n => !n.read).length;
  const displayNotifications = notifications.slice(0, 5); // Show top 5
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node) && notificationButtonRef.current && !notificationButtonRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setIsOpen, notificationButtonRef]);
  // Close dropdown when pressing Escape
  useEffect(() => {
    const handleEscapeKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscapeKey);
    return () => {
      document.removeEventListener('keydown', handleEscapeKey);
    };
  }, [setIsOpen]);
  if (!isOpen) return null;
  return <div ref={dropdownRef} className={`absolute right-0 w-80 sm:w-96 mt-2 rounded-lg shadow-lg z-50 overflow-hidden ${isAfterTide ? 'bg-[#1a1a2e] border border-[#16213e]' : dk.isDark ? 'bg-gray-800 ring-1 ring-gray-700' : 'bg-white ring-1 ring-black ring-opacity-5'}`} role="dialog" aria-modal="true" aria-label="Notifications" data-id="element-2792" data-mp="33jofqj1e64t3">
      <div className={`px-4 py-3 border-b flex justify-between items-center ${isAfterTide ? 'border-[#16213e]' : dk.borderSubtle}`} data-id="element-2793" data-mp="2kkk6b95xu90w">
        <h3 className={`font-semibold ${dk.textPrimary}`} data-id="element-2794" data-mp="26ugqazxnjbjw">Notifications</h3>
        {unreadCount > 0 && <button className={`text-xs flex items-center transition-colors ${rt.accentLink}`} onClick={() => {
        // Handle mark all read
      }} data-id="element-2795" data-mp="2avaeluf0asdb">
            <CheckIcon className="h-3 w-3 mr-1" data-id="element-2796" data-mp="21tjb8w7xuxmm" />
            Mark all read
          </button>}
      </div>

      <div className="max-h-[60vh] overflow-y-auto" data-id="element-2797" data-mp="1i8j0qgbno3wf">
        {displayNotifications.length > 0 ? <div className="flex flex-col" data-id="element-2798" data-mp="1dbajqj28u5wr">
            {displayNotifications.map(notification => <NotificationCard key={notification.id} notification={notification} variant="compact" onAction={(id, action) => {
          console.log(`Action ${action} on notification ${id}`);
          // Handle action
        }} data-id="element-2799" data-mp="1s4drjyq195aa" />)}
          </div> : <div className={`px-4 py-8 text-center ${dk.textMuted}`} data-id="element-2800" data-mp="2awextm54zxtq">
            No new notifications
          </div>}
      </div>

      <div className={`border-t ${isAfterTide ? 'border-[#16213e] bg-[#1a1a2e]' : dk.isDark ? 'border-gray-700 bg-gray-800' : 'border-gray-100 bg-gray-50'}`} data-id="element-2801" data-mp="2jxmjn4qgv66x">
        <Link to="/notifications" className={`block w-full text-center py-3 text-sm font-medium transition-colors ${rt.accentLink}`} onClick={() => setIsOpen(false)} data-id="element-2802" data-mp="2fxvu14coew3z">
          View All Notifications
        </Link>
      </div>
    </div>;
};
export default NotificationDropdown;